import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Mail, Phone, Send, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SITE_OPTIONS = [
  'Site Vitrine',
  'Site Vitrine 100% Personnalisé',
  'Site avec Réservation',
];

const PLAN_OPTIONS = [
  'Essentiel (34€/mois)',
  'Pro (49€/mois)',
  'Premium (89€/mois)',
];

const inputClasses =
  'h-12 bg-gray-900 border-gray-600 text-white placeholder:text-gray-400 focus-visible:ring-orange-500';

const ContactForm = ({ contact }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    siteType: '',
    subscription: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelect = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setShowSuccess(true);
      toast.success('Message envoyé avec succès !');
      setFormData({
        name: '',
        email: '',
        phone: '',
        siteType: '',
        subscription: '',
        message: '',
      });
    } catch (error) {
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 lg:py-32 bg-gray-900">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            {contact.title}
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            {contact.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div className="space-y-8 order-2">
            <div>
              <h3 className="text-2xl font-bold text-white mb-6">
                Informations de contact
              </h3>
              <p className="text-gray-400 mb-8 text-lg">
                Vous avez un projet en tête ? N'hésitez pas à me contacter. Je vous répondrai dans les plus brefs délais.
              </p>
            </div>

            <div className="space-y-6">
              <Card className="border-l-4 border-l-orange-500 hover:shadow-lg hover:shadow-orange-500/20 transition-shadow bg-gray-800 border-gray-700">
                <CardContent className="flex items-center gap-4 p-6">
                  <div className="flex-shrink-0 w-12 h-12 bg-orange-500/20 rounded-full flex items-center justify-center">
                    <Mail className="w-6 h-6 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Email</p>
                    <a
                      href={`mailto:${contact.email}`}
                      className="text-white font-semibold hover:text-orange-400 transition-colors"
                    >
                      {contact.email}
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-yellow-500 hover:shadow-lg hover:shadow-yellow-500/20 transition-shadow bg-gray-800 border-gray-700">
                <CardContent className="flex items-center gap-4 p-6">
                  <div className="flex-shrink-0 w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center">
                    <Phone className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Téléphone</p>
                    <a
                      href={`tel:${contact.phone}`}
                      className="text-white font-semibold hover:text-yellow-400 transition-colors"
                    >
                      {contact.phone}
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Contact Form */}
          <Card className="shadow-xl bg-gray-800 border-gray-700 order-1">
            <CardHeader>
              <CardTitle className="text-2xl text-white">Envoyez-moi un message</CardTitle>
              <CardDescription className="text-gray-400">
                Remplissez le formulaire ci-dessous et je vous contacterai rapidement
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-200">Nom complet *</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Jean Dupont"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className={inputClasses}
                    data-testid="contact-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-200">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="jean.dupont@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className={inputClasses}
                    data-testid="contact-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-gray-200">Téléphone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="06 12 34 56 78"
                    value={formData.phone}
                    onChange={handleChange}
                    className={inputClasses}
                    data-testid="contact-phone-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-200">Type de site souhaité</Label>
                  <Select value={formData.siteType} onValueChange={(v) => handleSelect('siteType', v)}>
                    <SelectTrigger
                      className="h-12 bg-gray-900 border-gray-600 text-white data-[placeholder]:text-gray-400 focus:ring-orange-500"
                      data-testid="contact-sitetype-select"
                    >
                      <SelectValue placeholder="Choisissez un type de site" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-gray-700 text-white">
                      {SITE_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt} className="text-white focus:bg-gray-800 focus:text-orange-400">
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-200">Formule de maintenance</Label>
                  <Select value={formData.subscription} onValueChange={(v) => handleSelect('subscription', v)}>
                    <SelectTrigger
                      className="h-12 bg-gray-900 border-gray-600 text-white data-[placeholder]:text-gray-400 focus:ring-orange-500"
                      data-testid="contact-subscription-select"
                    >
                      <SelectValue placeholder="Choisissez une formule" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-gray-700 text-white">
                      {PLAN_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt} className="text-white focus:bg-gray-800 focus:text-orange-400">
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-gray-200">Votre message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Décrivez-moi votre projet..."
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="resize-none bg-gray-900 border-gray-600 text-white placeholder:text-gray-400 focus-visible:ring-orange-500"
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit-button"
                  className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white rounded-full py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                >
                  {isSubmitting ? (
                    'Envoi en cours...'
                  ) : (
                    <>
                      Envoyer le message
                      <Send className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Success Popup */}
      <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
        <DialogContent
          className="max-w-md bg-gray-900 border-gray-700 text-center p-10"
          data-testid="contact-success-dialog"
        >
          <DialogHeader className="items-center">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mb-4 animate-in zoom-in duration-300">
              <CheckCircle2 className="w-12 h-12 text-green-400" />
            </div>
            <DialogTitle className="text-2xl font-bold text-white">
              Message envoyé !
            </DialogTitle>
            <DialogDescription className="text-base text-gray-300 mt-2">
              Merci pour votre demande. Je l'ai bien reçue et je la traiterai dans un délai de 72 heures.
            </DialogDescription>
          </DialogHeader>
          <Button
            onClick={() => setShowSuccess(false)}
            data-testid="contact-success-close-button"
            className="mt-4 mx-auto bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white rounded-full px-8 py-5 text-base font-semibold"
          >
            Parfait, merci !
          </Button>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ContactForm;
